import { BOND_TYPE_ENUM, tenorToYears } from 'lib/constants';
import {
    aud3mCurveName,
    isAudIrsVs3M,
    isAudIrsVs6M,
    isMixedMultiLeg
} from '@/helper/aud-mix-match.helper';
import { getLhsAudLegTenors, LHS_TENOR_SECTIONS } from '@/helper/lhs-matching.helper';

const AUD_IRS_SHORT_TENOR_YEARS = 3;
const AUD_IRS_VS_3M = 'AUD IRS (Vs 3M)';
const AUD_IRS_VS_6M = 'AUD IRS (Vs 6M)';
const AUD_VS_SUFFIX_PATTERN = /\(\s*Vs\s+\d+M\s*\)/i;

export const getAudLegCurveLabel = (tenor) => (
    tenorToYears(tenor) <= AUD_IRS_SHORT_TENOR_YEARS ? 'QQ' : 'SS'
);

const stripAudVsSuffix = (name) => (
    name ? name.replace(AUD_VS_SUFFIX_PATTERN, '').trim() || name : ''
);

const parseMatchingLegTenors = (item) => {
    const { t } = resolveMatchingStructureMeta(item);
    if (!t) return [];
    return t.split(' x ').map((leg) => leg.trim()).filter(Boolean);
};

const isAudOisCurveName = (name) => /^AUD\b/i.test(name || '') && /\bOIS\b/i.test(name);

export const isAudIrsMatchingCurve = (curve) => {
    const name = typeof curve === 'string' ? curve : curve?.name;
    if (!name || !/^AUD\b/i.test(name)) return false;
    if (isAudOisCurveName(name)) return false;
    if (name === 'AUD') return true;
    return /\bIRS\b/i.test(name);
};

export const shouldApplyAudDomesticConvention = ({
    useDomesticConvention = false,
    isAudMixMatch = false,
    curveName,
    selectedCurve
} = {}) => {
    if (!useDomesticConvention) return false;
    const selectedName = typeof selectedCurve === 'string' ? selectedCurve : selectedCurve?.name;
    if (selectedName && isAudOisCurveName(selectedName)) return false;
    if (isAudMixMatch) return true;
    if (curveName && isAudIrsMatchingCurve(curveName)) return true;
    if (isAudIrsMatchingCurve(selectedCurve)) return true;
    return !selectedCurve;
};

const isAudGenericIrsMatchingItem = (item) => {
    const { n, gs } = item || {};
    if (!n || !gs) return false;
    if (n.endsWith(' BOB') || n.endsWith(' 6s3s')) return false;
    return isAudIrsMatchingCurve(n);
};

export const resolveAudMatchingSectorFromTenors = (tenors, { n = 'AUD IRS', useDomesticConvention = false } = {}) => (
    resolveAudMatchingSector({ n, t: tenors.join(' x '), gs: true }, { useDomesticConvention })
);

export const resolveAudMatchingSector = (item, { useDomesticConvention = false } = {}) => {
    if (!isAudGenericIrsMatchingItem(item)) return null;

    const useConvention = useDomesticConvention || Boolean(item.isAudMixMatch);
    const tenors = parseMatchingLegTenors(item);
    const labels = tenors.map((tenor) => getAudLegCurveLabel(tenor));
    const isMixedLegs = labels.includes('QQ') && labels.includes('SS');
    const baseName = stripAudVsSuffix(item.n) || 'AUD IRS';

    if (useConvention) {
        if (isMixedLegs || item.isAudMixMatch) {
            return {
                sectorType: 'mixed',
                groupDisplayName: baseName,
                isMixedConvention: true
            };
        }
        if (!tenors.length) return null;
        const hasQQ = labels.includes('QQ');
        const hasSS = labels.includes('SS');
        if (hasSS && !hasQQ) {
            return {
                sectorType: 'vs6m',
                groupDisplayName: AUD_IRS_VS_6M,
                isMixedConvention: false
            };
        }
        return {
            sectorType: 'vs3m',
            groupDisplayName: AUD_IRS_VS_3M,
            isMixedConvention: false
        };
    }

    if (AUD_VS_SUFFIX_PATTERN.test(item.n || '')) {
        const isVs6m = /\(\s*Vs\s+6M\s*\)/i.test(item.n);
        return {
            sectorType: isVs6m ? 'vs6m' : 'vs3m',
            groupDisplayName: isVs6m ? AUD_IRS_VS_6M : AUD_IRS_VS_3M,
            isMixedConvention: false
        };
    }

    return null;
};

export const isAudMixedConventionTenors = (tenors) => isMixedMultiLeg(tenors);

export const isAudMixMatchMatchingItem = (item) => {
    if (item?.isAudMixMatch) return true;
    if (!item?.gs || !item?.n || !isAudIrsMatchingCurve(item.n)) return false;
    if (AUD_VS_SUFFIX_PATTERN.test(item.n)) return false;
    if (!item.t) return false;
    const tenors = item.t.split(' x ').map((leg) => leg.trim()).filter(Boolean);
    return isAudMixedConventionTenors(tenors);
};

export const resolveAudMixMatchCreateFlag = ({
    legTenors,
    useDomesticConvention,
    isBobOr6s3s = false,
    curveName
}) => {
    if (isBobOr6s3s) return undefined;
    if (!curveName || !isAudIrsMatchingCurve(curveName)) return undefined;
    if (useDomesticConvention) {
        return !!isAudMixedConventionTenors(legTenors);
    }
    return false;
};

export const getGenericStructureLegTenors = (data) => {
    if (!data) return [];
    if (data.end2Label) {
        return [data.end1Label, data.end2Label, data.endLabel].filter(Boolean);
    }
    if (data.end1Label) {
        return [data.end1Label, data.endLabel].filter(Boolean);
    }
    if (data.endLabel) return [data.endLabel];
    return [];
};

const resolveAudPhysicalCurveName = (itemCurveName, legTenors) => {
    if (itemCurveName?.includes(' / ')) {
        return itemCurveName.split(' / ')[0].trim();
    }
    if (itemCurveName) return itemCurveName;
    const labels = legTenors.map((tenor) => getAudLegCurveLabel(tenor));
    if (labels.includes('SS') && !labels.includes('QQ')) return AUD_IRS_VS_6M;
    return AUD_IRS_VS_3M;
};

export const resolveGridGenericOpenContext = (item, legTenors = []) => {
    const n = item?.n || '';
    if (n.endsWith(' BOB') || n.endsWith(' 6s3s')) {
        return { curveName: n, useDomesticConvention: false };
    }
    if (!isAudGenericIrsMatchingItem(item)) {
        return { curveName: n, useDomesticConvention: false };
    }
    const tenors = legTenors.length ? legTenors : parseMatchingLegTenors(item);
    if (item.isAudMixMatch || isAudMixedConventionTenors(tenors)) {
        return { useDomesticConvention: true, isAudMixMatch: true };
    }
    return {
        curveName: n.includes(' / ') ? n.split(' / ')[0].trim() : n,
        useDomesticConvention: false
    };
};

export const resolveLhsOpenMatchSetupContext = ({
    item,
    sectionId,
    currency
}) => {
    if (sectionId === LHS_TENOR_SECTIONS.BOB_OUTRIGHT) {
        return { irsId: item.irsId, curveName: `${currency} BOB`, useDomesticConvention: false };
    }
    if (sectionId === LHS_TENOR_SECTIONS.SIXS3S_OUTRIGHT) {
        return { irsId: item.irsId, curveName: `${currency} 6s3s`, useDomesticConvention: false };
    }

    if (currency !== 'AUD') {
        return {
            irsId: item.irsId,
            curveName: item.curveName || item.tag,
            useDomesticConvention: false
        };
    }

    const curveName = item.curveName || item.tag || '';
    if (isAudOisCurveName(curveName)) {
        return {
            irsId: item.irsId,
            curveName,
            useDomesticConvention: false
        };
    }

    const legTenors = getLhsAudLegTenors(item, sectionId);
    const isMixed = isAudMixedConventionTenors(legTenors);

    if (isMixed) {
        return {
            irsId: item.irsId,
            curveName: AUD_IRS_VS_3M,
            isAudMixMatch: true,
            useDomesticConvention: true
        };
    }

    return {
        irsId: item.irsId,
        curveName: item.curveName || curveName,
        useDomesticConvention: false
    };
};

export const resolveAudConventionLaunchIrsId = (tenors, irsCurves = []) => {
    if (!tenors?.length || !irsCurves?.length) return null;

    const aud3m = irsCurves.find((c) => (
        isAudIrsVs3M(c?.name)
        || c?.name === aud3mCurveName
        || c?.name === AUD_IRS_VS_3M
        || (c?.name === 'AUD' && (isAudIrsVs3M(c?.dcPhysicalName) || !c?.dcPhysicalName))
    ));
    const aud6m = irsCurves.find((c) => (
        isAudIrsVs6M(c?.name)
        || c?.name === AUD_IRS_VS_6M
    ));
    if (!aud3m?.id || !aud6m?.id) return null;
    if (isMixedMultiLeg(tenors)) return aud3m.id;
    const labels = tenors.map((tenor) => getAudLegCurveLabel(tenor));
    if (labels.includes('SS') && !labels.includes('QQ')) return aud6m.id;
    return aud3m.id;
};

export const getGenericDialogLegTenors = ({
    selectedStructureType, end, leg1, leg2
}) => {
    if (selectedStructureType === 'spread') return [leg1?.label, end?.label].filter(Boolean);
    if (selectedStructureType === 'butterfly') return [leg1?.label, leg2?.label, end?.label].filter(Boolean);
    return end?.label ? [end.label] : [];
};

const inferMatchingTenorLegs = (structure) => {
    if (!structure || typeof structure !== 'string' || !structure.includes(' | ')) return null;
    const legsPart = structure.split(' | ')[1]?.trim();
    if (!legsPart) return null;
    if (/\s+[Xx]\s+/.test(legsPart)) {
        return legsPart.split(/\s+[Xx]\s+/).map((leg) => leg.trim().toLowerCase()).join(' x ');
    }
    return legsPart.toLowerCase();
};

const resolveMatchingStructureMeta = (item) => ({
    gs: item.gs != null ? item.gs : Boolean(item.s?.includes(' | ')),
    t: item.t ?? inferMatchingTenorLegs(item.s)
});

const isEfpSpreadName = (n) => n?.endsWith(' EFP Spread');

const isVersionedAudIrsName = (n) => AUD_VS_SUFFIX_PATTERN.test(n || '');

const formatAudConventionLegSuffix = (tenorLegs) => (
    tenorLegs.split(' x ').map((leg) => getAudLegCurveLabel(leg.trim())).join(' ')
);

export const isLiveMatching = (matching, serverTime) => {
    if (!matching?.et) return false;
    const endTime = new Date(matching.et).getTime();
    if (Number.isNaN(endTime)) return false;
    const now = serverTime ?? Date.now();
    return endTime > now;
};

const resolveAudMatchingDisplayNames = (item) => {
    if (!item) return { grid: '', interest: '', trade: '' };
    if (item.cn) return { grid: item.cn, interest: item.cn, trade: item.cn };

    const { n } = item;
    if (!n) return { grid: '', interest: '', trade: '' };

    if (isEfpSpreadName(n)) return { grid: n, interest: n, trade: n };

    if (n.endsWith(' BOB') || n.endsWith(' 6s3s')) {
        const short = n.replace(/^[A-Z]{3} /, '');
        return { grid: short, interest: short, trade: n };
    }

    const { gs, t } = resolveMatchingStructureMeta(item);

    if (!gs && /^AUD\b/.test(n) && /\bOIS\b/i.test(n)) {
        return { grid: 'OIS', interest: 'OIS', trade: 'OIS' };
    }

    if (!/^AUD\b/.test(n)) {
        return { grid: n, interest: n, trade: n };
    }

    const versioned = isVersionedAudIrsName(n);

    if (gs && t) {
        const tenors = t.split(' x ').map((leg) => leg.trim()).filter(Boolean);
        if (isAudIrsMatchingCurve(n) && isAudMixedConventionTenors(tenors)) {
            const baseName = stripAudVsSuffix(n) || 'AUD IRS';
            const legs = formatAudConventionLegSuffix(t);
            const convention = `${baseName} (${legs})`;
            return { grid: legs, interest: convention, trade: convention };
        }
        if (versioned) {
            const gridShort = n.trim();
            return { grid: gridShort, interest: n, trade: n };
        }
        if (isAudIrsMatchingCurve(n)) {
            const baseName = stripAudVsSuffix(n) || 'AUD IRS';
            const legs = formatAudConventionLegSuffix(t);
            const convention = `${baseName} (${legs})`;
            return { grid: legs, interest: convention, trade: convention };
        }
        const gridShort = n.replace(AUD_VS_SUFFIX_PATTERN, '').trim() || n.trim();
        return { grid: gridShort, interest: n, trade: n };
    }

    if (versioned) {
        const gridShort = n.trim();
        return { grid: gridShort, interest: n, trade: n };
    }

    const baseName = stripAudVsSuffix(n) || n;
    const gridShort = n.replace(AUD_VS_SUFFIX_PATTERN, '').trim() || n.trim();
    return { grid: gridShort, interest: baseName, trade: baseName };
};

export const getMatchingCurveDisplayName = (item) => resolveAudMatchingDisplayNames(item).grid;

export const getMatchingCurveInterestDisplayName = (item) => resolveAudMatchingDisplayNames(item).interest;

export const getMatchingGroupDisplayName = (name) => {
    if (!name) return name;
    if (name.endsWith(' BOB') || name.endsWith(' 6s3s') || isEfpSpreadName(name)) return name;
    if (/^AUD\b/.test(name)) {
        return name.replace(/\s*\(Vs \d+M\)/i, '').trim() || name;
    }
    return name;
};

export const getMatchingTradeDisplayName = (item) => resolveAudMatchingDisplayNames(item).trade;

export const getIrsMatchingBondType = ({ isBobSelected, is6s3sSelected, selectedStructureType }) => {
    const selectedTab = selectedStructureType || '';
    if (isBobSelected) {
        const bobMap = {
            outright: BOND_TYPE_ENUM.BOB_OUTRIGHT,
            spread: BOND_TYPE_ENUM.BOB_SPREAD,
            butterfly: BOND_TYPE_ENUM.BOB_BUTTERFLY
        };
        return bobMap[selectedTab] ?? null;
    }

    if (is6s3sSelected) {
        const sixsMap = {
            outright: BOND_TYPE_ENUM.SIXS_OUTRIGHT,
            spread: BOND_TYPE_ENUM.SIXS_SPREAD,
            butterfly: BOND_TYPE_ENUM.SIXS_BUTTERFLY
        };
        return sixsMap[selectedTab] ?? null;
    }

    return null;
};

export const getBob6s3sSubtitle = ({
    isBobSelected, is6s3sSelected, selectedIrsCurveId, irsBobCurves
}) => {
    if (!isBobSelected && !is6s3sSelected) return null;
    if (!selectedIrsCurveId || !irsBobCurves?.length) return null;
    const curve = irsBobCurves.find((item) => item.id === selectedIrsCurveId);
    if (!curve) return null;
    const legName = isBobSelected ? curve.bobName : curve.sixsName;
    if (!legName) return null;
    return `${legName} vs ${curve.name}`;
};

export const getIrsMatchingCurveLabel = ({
    isBobSelected, is6s3sSelected, selectedIrsCurve, isMixMatchCurveSelected = false
}) => {
    if (!selectedIrsCurve) return '';
    if (is6s3sSelected) return `${selectedIrsCurve.currency} 6s3s`;
    if (isBobSelected) return `${selectedIrsCurve.currency} BOB`;
    if (isMixMatchCurveSelected) return `${selectedIrsCurve.currency} IRS`;
    if (selectedIrsCurve.name === 'AUD' && selectedIrsCurve.dcPhysicalName) {
        return selectedIrsCurve.dcPhysicalName;
    }
    return selectedIrsCurve.name ?? '';
};

export const filterIrsMatchingBobCurves = ({
    irsBobCurves, irsCurves, isBobSelected, is6s3sSelected
}) => {
    if (is6s3sSelected) return irsBobCurves?.filter((i) => i.sixsName) ?? [];
    if (isBobSelected) return irsBobCurves ?? [];
    return irsCurves ?? [];
};

export const getMatchingGroupKey = (item, options = {}) => {
    const {
        irsId, n, gk, currency, futureId, futureName
    } = item;
    if (futureId || futureName || isEfpSpreadName(n)) {
        const c = currency || (n?.split(' ')?.[0] ?? '');
        return `${c}::${n}`;
    }
    const groupingOptions = {
        ...options,
        useDomesticConvention: options.useDomesticConvention || Boolean(item.isAudMixMatch)
    };
    const sector = resolveAudMatchingSector(item, groupingOptions);
    if (sector) {
        const sectorName = sector.groupDisplayName;
        return `${sectorName}::${sectorName}`;
    }
    if (n && isAudIrsMatchingCurve(n) && !n.endsWith(' BOB') && !n.endsWith(' 6s3s')) {
        const name = AUD_VS_SUFFIX_PATTERN.test(n) ? n : (stripAudVsSuffix(n) || n);
        return `${name}::${name}`;
    }
    return gk ?? `${irsId}::${n}`;
};

export const findMatchingGroup = (rowData, {
    irsId, groupName, legTenors, useDomesticConvention = false
}) => {
    if (legTenors?.length && useDomesticConvention) {
        const sector = resolveAudMatchingSectorFromTenors(legTenors, { n: groupName || 'AUD IRS', useDomesticConvention });
        if (sector) {
            const groupKey = `${sector.groupDisplayName}::${sector.groupDisplayName}`;
            const byKey = rowData.find((r) => r.groupKey === groupKey);
            if (byKey) return byKey;
        }
    }
    if (!irsId || !groupName) return undefined;
    const groupKey = getMatchingGroupKey({ irsId, n: groupName }, { useDomesticConvention });
    return rowData.find((r) => r.groupKey === groupKey)
        ?? rowData.find((r) => r.irsId === irsId && r.name === groupName);
};

export const findLiveMatching = (rowData, {
    irsId, groupName, legTenors, predicate, useDomesticConvention = false
}) => {
    const group = findMatchingGroup(rowData, {
        irsId, groupName, legTenors, useDomesticConvention
    });
    return group?.matchings.find(predicate);
};

export const findMatchingById = (rowData, matchingId) => {
    if (!matchingId) return null;
    for (const group of rowData ?? []) {
        const match = group.matchings?.find((m) => m.id === matchingId);
        if (match) return match;
    }
    return null;
};

export const buildMatchingGroupRow = (item, options = {}) => {
    const groupingOptions = {
        ...options,
        useDomesticConvention: options.useDomesticConvention || Boolean(item.isAudMixMatch)
    };
    const sector = resolveAudMatchingSector(item, groupingOptions);
    return {
        groupKey: getMatchingGroupKey(item, groupingOptions),
        irsId: item.irsId,
        name: sector?.groupDisplayName ?? item.n,
        matchings: []
    };
};

export const getActiveMatchings = (rowData, serverTime, groupKey) => {
    if (!serverTime || !rowData?.length) return [];
    const isActive = (m) => new Date(m.et).getTime() > serverTime;
    if (!groupKey) {
        return rowData.flatMap((r) => r.matchings.filter(isActive));
    }
    const group = rowData.find((r) => r.groupKey === groupKey);
    return group?.matchings.filter(isActive) ?? [];
};

export const getMatchingsForCopy = (rowData, serverTime, groupKey, mode) => {
    const active = getActiveMatchings(rowData, serverTime, groupKey);
    if (mode === 'liveInterest') {
        return active.filter((m) => m.interest);
    }
    return active;
};

export const getMidPriceUnit = (matching) => {
    const {
        start1, gs, t, n
    } = matching;
    const useBps = start1 || (gs && (t?.includes('x') || n?.endsWith('BOB') || n?.endsWith('6s3s')));
    return useBps ? 'bps' : '%';
};

export const formatTimeLeft = (matching, serverTime) => {
    if (!matching?.et || serverTime == null) return '-';
    const remainingMs = new Date(matching.et).getTime() - serverTime;
    if (remainingMs <= 0) return '-';
    const remainingSec = Math.floor(remainingMs / 1000);
    const mins = Math.floor(remainingSec / 60);
    const secs = remainingSec % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const formatMidPriceForCopy = (matching) => {
    const { p } = matching;
    if (p === null || p === undefined || p === '') return '-';
    return `${p} ${getMidPriceUnit(matching)}`;
};

const formatCurveNameForCopy = (matching, markLiveInterest) => {
    const name = matching.n || '-';
    if (markLiveInterest && matching.interest) {
        return `[LI] ${name}`;
    }
    return name;
};

export const formatMatchingsCopyText = (matchings, serverTime, { markLiveInterest = false, emptyMessage, headerGap = true, titleInFence = false } = {}) => {
    const spacer = '  ';
    const columns = {
        curveName: 'Curve Name',
        structure: 'Structure',
        midPrice: 'Mid Price',
        timeLeft: 'Time Left'
    };

    const buildAlignedTable = (rows) => {
        let maxNameLen = columns.curveName.length;
        let maxStructLen = columns.structure.length;
        let maxMidLen = columns.midPrice.length;
        let maxTimeLen = columns.timeLeft.length;

        rows.forEach((row) => {
            maxNameLen = Math.max(maxNameLen, row.curveName.length);
            maxStructLen = Math.max(maxStructLen, row.structure.length);
            maxMidLen = Math.max(maxMidLen, row.midPrice.length);
            maxTimeLen = Math.max(maxTimeLen, row.timeLeft.length);
        });

        const formatRow = (row) => [
            row.curveName.padEnd(maxNameLen),
            row.structure.padEnd(maxStructLen),
            row.midPrice.padEnd(maxMidLen),
            row.timeLeft.padEnd(maxTimeLen)
        ].join(spacer);

        const header = formatRow(columns);
        if (!rows.length) return header;
        return `${header}\n${headerGap ? '\n' : ''}${rows.map(formatRow).join('\n')}`;
    };

    if (!matchings?.length) {
        if (!emptyMessage) return null;
        const header = buildAlignedTable([]);
        return `Live Matches\n\n\`\`\`\n${header}\n\n${emptyMessage}\n\`\`\``;
    }

    const rows = matchings.map((m) => ({
        curveName: formatCurveNameForCopy(m, markLiveInterest),
        structure: m.s || '-',
        midPrice: formatMidPriceForCopy(m),
        timeLeft: formatTimeLeft(m, serverTime)
    }));

    const table = buildAlignedTable(rows);
    return titleInFence
        ? `\`\`\`\n\nLive Matches\n\n${table}\n\n\`\`\``
        : `Live Matches\n\n\`\`\`\n${table}\n\`\`\``;
};
